<template>
  <el-card shadow="never" class="page-card">
    <div slot="header" class="card-header">
      <div class="card-title">
        <span class="page-icon">🏆</span>
        <span>玩家关卡</span>
      </div>
      <el-button type="primary" size="small" icon="el-icon-refresh" :loading="loading" @click="loadList">
        刷新
      </el-button>
    </div>

    <el-table v-loading="loading" :data="list" stripe border style="width: 100%">
      <el-table-column label="缩略图" width="96" align="center">
        <template slot-scope="{ row }">
          <div class="thumb-cell">
            <el-image
              v-if="row.thumbSrc"
              class="thumb-img"
              :src="row.thumbSrc"
              fit="cover"
              :preview-src-list="[row.thumbSrc]"
            >
              <div slot="error" class="thumb-fallback">失败</div>
            </el-image>
            <div v-else-if="row.thumbLoading" class="thumb-fallback">加载中</div>
            <div v-else class="thumb-fallback" @click="loadRowThumb(row)">点加载</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="id" label="关卡 ID" min-width="280" show-overflow-tooltip />
      <el-table-column label="难度" width="100" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="difficultyTagType(row.difficulty)" size="small">
            {{ difficultyLabel(row.difficulty) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="网格" width="120" align="center">
        <template slot-scope="{ row }">{{ row.cols }} × {{ row.rows }}</template>
      </el-table-column>
      <el-table-column prop="tile_count" label="块数" width="80" align="center" />
      <el-table-column prop="created_at" label="创建时间" min-width="180" show-overflow-tooltip />
      <el-table-column label="操作" width="88" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="danger" size="mini" plain :loading="deletingId === row.id" @click="onDelete(row)">
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-if="!loading && list.length === 0" description="暂无数据" />
  </el-card>
</template>

<script>
import { fetchPlayerLevels, deletePlayerLevel, fetchPlayerLevelPreview } from '@/api/playerLevel'
import { Message, MessageBox } from 'element-ui'

export default {
  name: 'PlayerLevels',
  data() {
    return {
      list: [],
      loading: false,
      deletingId: ''
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    difficultyLabel(d) {
      const m = { low: '低', medium: '中', high: '高' }
      return m[d] || d || '-'
    },
    difficultyTagType(d) {
      const m = { low: 'success', medium: 'warning', high: 'danger' }
      return m[d] || 'info'
    },
    async loadList() {
      this.loading = true
      try {
        const res = await fetchPlayerLevels()
        if (res.success) {
          this.list = (res.levels || []).map((r) => ({ ...r, thumbSrc: '', thumbLoading: false }))
        } else {
          this.list = []
          Message.error(res.error || '加载失败')
        }
      } catch (e) {
        this.list = []
      } finally {
        this.loading = false
        this.$nextTick(() => this.hydrateThumbs())
      }
    },
    async hydrateThumbs() {
      await Promise.all(
        this.list.map(async (row) => {
          this.$set(row, 'thumbLoading', true)
          try {
            const res = await fetchPlayerLevelPreview(row.id)
            if (res.success && res.original) {
              this.$set(row, 'thumbSrc', res.original)
            }
          } catch (e) {
            /* 忽略单条缩略图失败 */
          } finally {
            this.$set(row, 'thumbLoading', false)
          }
        })
      )
    },
    async loadRowThumb(row) {
      if (row.thumbSrc || row.thumbLoading) return
      this.$set(row, 'thumbLoading', true)
      try {
        const res = await fetchPlayerLevelPreview(row.id)
        if (res.success && res.original) {
          this.$set(row, 'thumbSrc', res.original)
        }
      } finally {
        this.$set(row, 'thumbLoading', false)
      }
    },
    onDelete(row) {
      MessageBox.confirm(
        `将逻辑删除该关卡并删除 MinIO 中的文件。关卡 ID：${row.id}`,
        '确认删除',
        {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
          confirmButtonClass: 'el-button--danger'
        }
      )
        .then(async () => {
          this.deletingId = row.id
          try {
            const res = await deletePlayerLevel(row.id)
            if (res.success) {
              Message.success('已删除')
              this.loadList()
            } else {
              Message.error(res.error || '删除失败')
            }
          } catch (e) {
            const msg =
              (e.response && e.response.data && e.response.data.error) ||
              e.message ||
              '删除失败'
            Message.error(msg)
          } finally {
            this.deletingId = ''
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.page-card {
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.page-icon {
  font-size: 20px;
  line-height: 1;
}

/* 缩略图 */
.thumb-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
}
.thumb-img {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  cursor: zoom-in;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
}
.thumb-fallback {
  width: 60px;
  height: 60px;
  line-height: 60px;
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  border: 1px dashed #d9dde4;
  transition: border-color 0.2s, color 0.2s;
}
.thumb-fallback:hover {
  border-color: #667eea;
  color: #667eea;
}
</style>
